<template>
    <main id="saved-ideas" class="saved-ideas-wrapper">
        <div class="list-banner">
            <div class="overlay"></div>
            <h1 class="banner-title">Saved Ideas</h1>
        </div>

        <SubMenu parent-route-name="tripPlanning" />

        <div class="max-width-container">
            <div class="planning-content">
                <LoadingScreen
                    v-if="loading"
                    message="Loading your saved trip ideas…"
                />

                <div v-else-if="error" class="error-state">
                    <p>{{ error }}</p>
                    <Bttn @click="fetchSavedIdeas">Try Again</Bttn>
                </div>

                <div v-else-if="ideas.length > 0">
                    <p v-if="actionError" class="planning-action-error">
                        {{ actionError }}
                    </p>
                    <SavedIdeasFilters
                        class="ideas-filters-bar"
                        :filters="filters"
                        :options="options"
                        :active-count="activeCount"
                        @update="setFilter"
                        @clear="clearFilters"
                    />

                    <div v-if="!filteredIdeas.length" class="no-matches-state">
                        <p>No ideas match these filters.</p>
                        <Bttn class="outlined" @click="clearFilters"
                            >Clear filters</Bttn
                        >
                    </div>

                    <div v-else class="recommendations-container">
                        <nav class="planning-nav">
                            <SavedIdeasNav
                                collapsible
                                :groups="groupedIdeas"
                                @select="goToIdea"
                            />
                        </nav>

                        <div class="recommendations">
                            <IdeaItem
                                v-for="idea in orderedIdeas"
                                :id="'idea-' + idea.id"
                                :key="idea.id"
                                :idea="idea"
                            >
                                <template #actions>
                                    <Bttn
                                        class="outlined danger"
                                        icon="delete"
                                        :icon-only="true"
                                        :aria-label="`Delete ${idea.title}`"
                                        @click="pendingDeleteId = idea.id"
                                    />
                                    <Share
                                        :item-id="idea.id"
                                        item-type="trip_idea"
                                        class="outlined"
                                    />
                                </template>
                                <a
                                    href="#saved-ideas"
                                    class="back-to-top"
                                    @click.prevent="scrollTo('#saved-ideas')"
                                >
                                    <span class="material-symbols-outlined"
                                        >arrow_upward</span
                                    >
                                    Back to top
                                </a>
                            </IdeaItem>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="ideas-fab"
                        @click="sheetOpen = true"
                    >
                        <span class="material-symbols-outlined">tune</span>
                        Filters<template v-if="activeCount">
                            ({{ activeCount }})</template
                        >
                        · {{ filteredIdeas.length }}
                        {{ filteredIdeas.length === 1 ? "idea" : "ideas" }}
                    </button>

                    <teleport to="body">
                        <div
                            v-if="sheetOpen"
                            class="ideas-sheet-overlay"
                            @click.self="sheetOpen = false"
                        >
                            <div class="ideas-sheet">
                                <div class="ideas-sheet-header">
                                    <span>
                                        {{ filteredIdeas.length }} of
                                        {{ ideas.length }} ideas
                                    </span>
                                    <button
                                        type="button"
                                        class="icon-button"
                                        aria-label="Close"
                                        @click="sheetOpen = false"
                                    >
                                        <span class="material-symbols-outlined"
                                            >close</span
                                        >
                                    </button>
                                </div>
                                <SavedIdeasFilters
                                    :filters="filters"
                                    :options="options"
                                    :active-count="activeCount"
                                    @update="setFilter"
                                    @clear="clearFilters"
                                />
                                <SavedIdeasNav
                                    :groups="groupedIdeas"
                                    @select="goToIdea"
                                />
                            </div>
                        </div>
                    </teleport>
                </div>

                <div v-else class="initial-state">
                    <p>
                        You don't have any saved trip ideas yet. Explore your
                        trip recommendations under "Inspirations" and save your
                        favorites!
                    </p>
                    <router-link to="/planning">
                        <Bttn class="outlined ai" icon="auto_awesome"
                            >Explore Inspirations</Bttn
                        >
                    </router-link>
                </div>
            </div>
        </div>
        <ConfirmModal
            v-if="pendingDeleteId !== null"
            title="Delete trip idea"
            message="Are you sure you want to delete this trip idea?"
            confirm-label="Delete"
            @close="pendingDeleteId = null"
            @confirm="deleteIdea"
        />
    </main>
</template>

<script>
import { useSavedIdeaStore } from "js/stores/savedIdeaStore";
import IdeaItem from "js/components/list-items/IdeaItem.vue";
import SavedIdeasFilters from "js/components/widgets/SavedIdeasFilters.vue";
import SavedIdeasNav from "js/components/widgets/SavedIdeasNav.vue";
import LoadingScreen from "js/components/common/LoadingScreen";
import ConfirmModal from "js/components/modals/ConfirmModal";

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
const LIST_FILTERS = ["duration", "continent", "region", "country", "month"];

const emptyFilters = () => ({
    q: "",
    duration: [],
    continent: [],
    region: [],
    country: [],
    month: [],
});

export default {
    name: "SavedIdeas",
    components: {
        IdeaItem,
        SavedIdeasFilters,
        SavedIdeasNav,
        LoadingScreen,
        ConfirmModal,
    },
    data() {
        return {
            loading: false,
            error: null,
            actionError: null,
            pendingDeleteId: null,
            savedIdeaStore: null,
            filters: emptyFilters(),
            sheetOpen: false,
        };
    },
    computed: {
        ideas() {
            return this.savedIdeaStore ? this.savedIdeaStore.list : [];
        },
        regionContinent() {
            return Object.fromEntries(
                this.$enumOptions("world_region").map(([code, , continent]) => [
                    code,
                    continent,
                ])
            );
        },
        countryRegion() {
            return Object.fromEntries(
                this.$enumOptions("country_world_region")
            );
        },
        durationBuckets() {
            return this.$enumOptions("travel_duration")
                .map(([value]) => {
                    const [min, max] = value.endsWith("+")
                        ? [parseInt(value), Infinity]
                        : value.split("-").map(Number);
                    return {
                        id: value,
                        name: `${value} ${value === "1" ? "day" : "days"}`,
                        min,
                        max: max ?? min,
                    };
                })
                .sort((a, b) => b.min - a.min);
        },
        options() {
            const count = (lists) => {
                const counts = {};
                lists.flat().forEach((value) => {
                    counts[value] = (counts[value] || 0) + 1;
                });
                return counts;
            };
            const toOptions = (counts, label) =>
                Object.keys(counts)
                    .map((id) => ({
                        id,
                        name: `${label(id)} (${counts[id]})`,
                        label: label(id),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

            const continents = this.filters.continent;
            const regions = this.filters.region;
            const countryAllowed = (code) => {
                if (!continents.length && !regions.length) {
                    return true;
                }
                const region = this.countryRegion[code];
                const continent = this.regionContinent[region];
                return (
                    regions.includes(region) ||
                    (continents.includes(continent) &&
                        !regions.some(
                            (r) => this.regionContinent[r] === continent
                        ))
                );
            };

            const regionCounts = count(
                this.ideas.map((idea) => idea.world_regions ?? [])
            );
            const countryCounts = count(
                this.ideas.map((idea) => idea.country_codes ?? [])
            );
            const monthCounts = count(
                this.ideas.map((idea) =>
                    (idea.ideal_months ?? []).map((m) =>
                        String(m).toLowerCase()
                    )
                )
            );

            return {
                duration: this.durationBuckets
                    .slice()
                    .reverse()
                    .map(({ id, name }) => ({ id, name })),
                continent: toOptions(
                    count(this.ideas.map((idea) => idea.continents ?? [])),
                    (id) => this.$enumGet("continent", id)
                ),
                region: toOptions(
                    Object.fromEntries(
                        Object.entries(regionCounts).filter(
                            ([id]) =>
                                !continents.length ||
                                continents.includes(this.regionContinent[id])
                        )
                    ),
                    (id) => this.$enumGet("world_region", id)
                ),
                country: toOptions(
                    Object.fromEntries(
                        Object.entries(countryCounts).filter(([id]) =>
                            countryAllowed(id)
                        )
                    ),
                    (id) => this.$enumGet("country", id)
                ),
                month: MONTHS.filter(
                    (month) => monthCounts[month.toLowerCase()]
                ).map((month) => ({
                    id: month,
                    name: `${month} (${monthCounts[month.toLowerCase()]})`,
                })),
            };
        },
        activeCount() {
            return (
                LIST_FILTERS.filter((key) => this.filters[key].length).length +
                (this.filters.q.trim() ? 1 : 0)
            );
        },
        matchesLocation() {
            const { continent, region, country } = this.filters;
            if (!continent.length && !region.length && !country.length) {
                return () => true;
            }
            const regionOf = (code) => this.countryRegion[code];
            const continentOf = (code) => this.regionContinent[regionOf(code)];
            return (code) => {
                if (country.includes(code)) {
                    return true;
                }
                const codeRegion = regionOf(code);
                const codeContinent = continentOf(code);
                if (
                    region.includes(codeRegion) &&
                    !country.some((c) => regionOf(c) === codeRegion)
                ) {
                    return true;
                }
                return (
                    continent.includes(codeContinent) &&
                    !region.some(
                        (r) => this.regionContinent[r] === codeContinent
                    ) &&
                    !country.some((c) => continentOf(c) === codeContinent)
                );
            };
        },
        filteredIdeas() {
            const query = this.filters.q.trim().toLowerCase();
            const months = this.filters.month.map((m) => m.toLowerCase());
            const hasLocationFilter =
                this.filters.continent.length ||
                this.filters.region.length ||
                this.filters.country.length;

            return this.ideas.filter((idea) => {
                if (query) {
                    const haystack = [
                        idea.title,
                        idea.short_title,
                        ...(idea.country_names ?? []),
                        ...(idea.locations ?? []),
                    ]
                        .join(" ")
                        .toLowerCase();
                    if (!haystack.includes(query)) {
                        return false;
                    }
                }
                if (
                    this.filters.duration.length &&
                    !this.filters.duration.includes(
                        this.bucketOf(idea.ideal_duration).id
                    )
                ) {
                    return false;
                }
                if (
                    months.length &&
                    !(idea.ideal_months ?? []).some((m) =>
                        months.includes(String(m).toLowerCase())
                    )
                ) {
                    return false;
                }
                return (
                    !hasLocationFilter ||
                    (idea.country_codes ?? []).some(this.matchesLocation)
                );
            });
        },
        groupedIdeas() {
            return this.durationBuckets
                .map((bucket) => ({
                    ...bucket,
                    items: this.filteredIdeas.filter(
                        (idea) => this.bucketOf(idea.ideal_duration) === bucket
                    ),
                }))
                .filter((group) => group.items.length);
        },
        orderedIdeas() {
            return this.groupedIdeas.flatMap((group) => group.items);
        },
    },
    watch: {
        filters(value) {
            this.$router.replace({ query: this.queryFromFilters(value) });
        },
    },
    created() {
        this.filters = this.filtersFromQuery(this.$route.query);
    },
    async mounted() {
        this.savedIdeaStore = useSavedIdeaStore();
        this.loading = true;
        this.error = null;
        try {
            await this.savedIdeaStore.load();
        } catch (err) {
            this.error = "Failed to load saved trip ideas.";
            console.error(err);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        async fetchSavedIdeas() {
            this.loading = true;
            this.error = null;
            try {
                this.savedIdeaStore.dirty = true;
                await this.savedIdeaStore.load();
            } catch (err) {
                this.error = "Failed to load saved trip ideas.";
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
        async deleteIdea() {
            const id = this.pendingDeleteId;
            this.pendingDeleteId = null;
            this.actionError = null;
            try {
                await this.savedIdeaStore.remove(id);
            } catch (err) {
                this.actionError =
                    "Failed to delete the trip idea. Please try again.";
                console.error(err);
            }
        },
        bucketOf(days) {
            const buckets = this.durationBuckets;
            return (
                buckets.find((b) => days >= b.min && days <= b.max) ??
                buckets[buckets.length - 1]
            );
        },
        setFilter(key, value) {
            if (JSON.stringify(this.filters[key]) === JSON.stringify(value)) {
                return;
            }
            const next = { ...this.filters, [key]: value };
            if (key === "continent") {
                const dropped = this.filters.continent.filter(
                    (c) => !value.includes(c)
                );
                next.region = next.region.filter(
                    (r) => !dropped.includes(this.regionContinent[r])
                );
                next.country = next.country.filter(
                    (c) =>
                        !dropped.includes(
                            this.regionContinent[this.countryRegion[c]]
                        )
                );
            } else if (key === "region") {
                const dropped = this.filters.region.filter(
                    (r) => !value.includes(r)
                );
                next.country = next.country.filter(
                    (c) => !dropped.includes(this.countryRegion[c])
                );
            }
            this.filters = next;
        },
        clearFilters() {
            this.filters = emptyFilters();
        },
        filtersFromQuery(query) {
            const filters = emptyFilters();
            filters.q = typeof query.q === "string" ? query.q : "";
            LIST_FILTERS.forEach((key) => {
                if (typeof query[key] === "string" && query[key]) {
                    filters[key] = query[key].split(",");
                }
            });
            return filters;
        },
        queryFromFilters(filters) {
            const query = {};
            if (filters.q.trim()) {
                query.q = filters.q;
            }
            LIST_FILTERS.forEach((key) => {
                if (filters[key].length) {
                    query[key] = filters[key].join(",");
                }
            });
            return query;
        },
        goToIdea(idea) {
            this.sheetOpen = false;
            this.$nextTick(() => this.scrollTo("#idea-" + idea.id));
        },
        scrollTo(selector) {
            const el = document.querySelector(selector);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        },
    },
};
</script>
